import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

const CATEGORY_ICONS = {
  ownership: '📋', encumbrance: '⚠️', corporate: '🏢', family: '👨‍👩‍👧‍👦',
  contact: '👤', market: '📊', negotiation: '💼', financial: '💰',
  geographic: '📍', legal: '⚖️',
};

const CONFIDENCE_COLOR = (c) => {
  if (c >= 0.95) return 'text-emerald-600';
  if (c >= 0.80) return 'text-blue-600';
  if (c >= 0.60) return 'text-amber-600';
  return 'text-red-500';
};

const CLASS_BADGE = {
  fact: { bg: 'bg-emerald-100 text-emerald-800', label: 'Fato' },
  inference: { bg: 'bg-amber-100 text-amber-800', label: 'Inferência' },
  action: { bg: 'bg-blue-100 text-blue-800', label: 'Ação' },
};

export default function KnowledgeSection({ entityType, entityId }) {
  const [atoms, setAtoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCat, setExpandedCat] = useState(null);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    supabase.from('v_entity_knowledge')
      .select('*')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('category')
      .order('valid_from', { ascending: false, nullsFirst: false })
      .then(({ data }) => { setAtoms(data || []); setLoading(false); });
  }, [entityType, entityId]);

  if (loading) return null;
  if (!atoms.length) return null;

  const grouped = {};
  atoms.forEach(a => {
    if (!grouped[a.category]) grouped[a.category] = [];
    grouped[a.category].push(a);
  });

  return (
    <section>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
        🧠 Conhecimento ({atoms.length})
      </div>
      <div className="space-y-1">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="border border-slate-100 rounded">
            <button
              onClick={() => setExpandedCat(expandedCat === cat ? null : cat)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 text-left"
            >
              <span>{CATEGORY_ICONS[cat] || '📄'}</span>
              <span className="font-medium capitalize">{cat}</span>
              <span className="text-slate-400">({items.length})</span>
              <span className="ml-auto text-[10px] text-slate-400">{expandedCat === cat ? '▼' : '▶'}</span>
            </button>
            {expandedCat === cat && (
              <div className="px-2 pb-2 space-y-1.5">
                {items.map(a => (
                  <div key={a.atom_id} className="text-[11px] text-slate-700 border-b border-slate-50 pb-1">
                    <div className="flex items-start gap-1.5">
                      <span className={`px-1 py-0.5 text-[8px] rounded ${CLASS_BADGE[a.knowledge_class]?.bg || 'bg-slate-100'}`}>
                        {CLASS_BADGE[a.knowledge_class]?.label || a.knowledge_class}
                      </span>
                      <span className="flex-1">{a.content}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-[9px] text-slate-400">
                      {a.source_filename && <span>📎 {a.source_filename}</span>}
                      {a.source_doc_type && <span>📄 {a.source_doc_type}</span>}
                      {!a.source_filename && a.source_type && <span>🔗 {a.source_type}</span>}
                      <span className={CONFIDENCE_COLOR(a.confidence)}>{(a.confidence * 100).toFixed(0)}%</span>
                      {a.valid_from && <span>📅 {a.valid_from}</span>}
                      {a.valid_to && <span>→ {a.valid_to}</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
